import React, { useState, useEffect } from "react"

import { useMenu } from "../../../contexts/MenuContext"
import { useHistory } from "react-router-dom"

import {
    Badge,
    Divider,
    IconButton,
    Menu,
    MenuItem
} from "@mui/material"

import NotificationsIcon from "@mui/icons-material/Notifications"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import CloseIcon from "@mui/icons-material/Close"
import DoneAll from "@mui/icons-material/DoneAll"
import DeleteSweep from "@mui/icons-material/DeleteSweep"
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive"
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff"
import VolumeOffIcon from "@mui/icons-material/VolumeOff"
import VolumeUpIcon from "@mui/icons-material/VolumeUp"
import ImageIcon from "@mui/icons-material/Image"
import VideocamIcon from "@mui/icons-material/Videocam"
import AudiotrackIcon from "@mui/icons-material/Audiotrack"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import EmailIcon from "@mui/icons-material/Email"

import { emojiReplaceJSX } from '../../../utils/emojiDict';
import { setFaviconBadge } from "../../../utils/favicon"
import { getSession } from "../../../auth"
import api from "../../../api"

import "./styles.css"


const getLevelIcon = (level) => {
    switch (level) {
        case "high":
            return <ErrorOutlineIcon />

        case "medium":
            return <WarningAmberIcon />

        case "success":
            return <CheckCircleOutlineIcon />

        default:
            return <InfoOutlinedIcon />
    }
}


const formatNotificationDate = (date) => {
    if (!date)
        return ""

    const value = new Date(date)
    value.setHours(value.getHours() - 3)
    const now = new Date()

    const diff = Math.floor((now - value) / 1000)

    if (diff < 60)
        return "Agora"

    if (diff < 3600)
        return `Há ${Math.floor(diff / 60)} minuto(s)`

    if (diff < 86400)
        return `Há ${Math.floor(diff / 3600)} hora(s)`

    if (diff < 172800)
        return "Ontem"

    return value.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    })
}


const NotificationsMenu = () => {

    const history = useHistory()

    const { realtimeNotification, activeChat } = useMenu()

    const [notifications, setNotifications] = useState([])
    const [notificationItems, setNotificationItems] = useState([])

    const [anchorEl, setAnchorEl] = useState(null)
    const [, setNow] = useState(Date.now())

    const [nextNotificationsUrl, setNextNotificationsUrl] = useState(null)
    const [loadingNotifications, setLoadingNotifications] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)

    const [soundEnabled, setSoundEnabled] = useState(() => {
        return localStorage.getItem("notificationsSoundEnabled") !== "false"
    })

    const [desktopNotificationsEnabled, setDesktopNotificationsEnabled] = useState(() => {
        return localStorage.getItem("desktopNotificationsEnabled") !== "false"
    })

    useEffect(() => {
        const interval = setInterval(() => {
            setNow(Date.now())
        }, 30000)

        return () => clearInterval(interval)
    }, [])

    /*
     * notifications now contains groups:
     *
     * {
     *     item: {...},
     *     items: [...],
     *     unreadCount: 2
     * }
     *
     * Therefore the badge must sum the unread notifications
     * from every group.
     */
    const unreadCount = notifications.reduce(
        (total, group) => total + group.unreadCount,
        0
    )


    const aggregateNotifications = (notifications) => {
        const groups = new Map()

        notifications.forEach(item => {
            const notification = item.notification

            const isMessage =
                notification.type === "queue_message" ||
                notification.type === "chat_message"

            if (!isMessage) {
                groups.set(`notification-${item.id}`, {
                    item,
                    items: [item],
                    unreadCount: item.read_at ? 0 : 1,
                })

                return
            }

            const uniqueId = notification.context?.unique_id

            if (!uniqueId) {
                groups.set(`notification-${item.id}`, {
                    item,
                    items: [item],
                    unreadCount: item.read_at ? 0 : 1,
                })

                return
            }

            const key = `${notification.accountcode}-${uniqueId}`

            if (!groups.has(key)) {
                groups.set(key, {
                    item,
                    items: [item],
                    unreadCount: item.read_at ? 0 : 1,
                })

                return
            }

            const group = groups.get(key)

            group.items.push(item)

            if (!item.read_at)
                group.unreadCount++

            if (
                new Date(item.notification.created_at) >
                new Date(group.item.notification.created_at)
            ) {
                group.item = item
            }
        })

        return Array.from(groups.values())
    }


    const notifyUser = (notification) => {
        if (soundEnabled) {
            const audio = new Audio(
                `${window.location.origin}/new_message.mp3`
            )
            audio.play().catch(() => {})
        }

        if (desktopNotificationsEnabled) {
            if (Notification.permission === "default") {
                Notification.requestPermission().then((permission) => {
                    if (permission !== "granted")
                        return

                    showBrowserNotification(notification)
                })

                return
            }

            if (Notification.permission === "granted") {
                showBrowserNotification(notification)
            }
        }
    }


    const showBrowserNotification = (notification) => {
        const browserNotification = new Notification(
            notification.title || "Nova notificação",
            {
                body: notification.subtitle || "",
                icon: `${window.location.origin}/kwik_favicon.png`,
                lang: "pt-BR",
                silent: true,
            }
        )

        browserNotification.onclick = async () => {
            window.focus()
            browserNotification.close()

            const item = {
                id: notification.id,
                notification,
                read_at: null,
                dismissed_at: null,
            }

            const group = {
                item,
                items: [item],
                unreadCount: 1,
            }

            await handleNotificationClick(group)
        }
    }


    const addRealtimeNotification = (notification) => {
        const type = notification.type

        const isMessage =
            type === "queue_message" ||
            type === "chat_message"

        const uniqueId = notification.context?.unique_id

        /*
         * Only suppress/auto-read notifications when this
         * particular browser tab is currently visible.
         *
         * If the user has another tab open and switches to it,
         * this tab becomes "hidden" and notifications remain unread.
         */
        const isCurrentTabActive =
            document.visibilityState === "visible" &&
            document.hasFocus()

        /*
         * Determine which interface the user is currently viewing.
         *
         * /chat?selectedTab=service
         *     -> Agent interface
         *
         * /chat?selectedTab=supervision
         *     -> Supervisor interface
         *
         * /chat
         *     -> Supervisor interface by default
         */
        const pathname = location.pathname

        const session = getSession()

        const isSupervisorUser =
            session.groups?.some(group =>
                group.name === "supervisor" ||
                group === "supervisor"
            )

        const params = new URLSearchParams(location.search)
        const selectedTab = params.get("selectedTab")

        const isChatInterface =
            location.pathname === "/chat" ||
            location.pathname === "/chat/"

        const isAgentInterface =
            isChatInterface &&
            (
                selectedTab === "service" ||
                (!selectedTab && !isSupervisorUser)
            )

        const isSupervisorInterface =
            isChatInterface &&
            (
                selectedTab === "supervision" ||
                (!selectedTab && isSupervisorUser)
            )

        /*
         * Supervisor notifications that should be automatically
         * marked as read while actively viewing the supervisor
         * interface.
         */
        const shouldAutoReadAsSupervisor =
            isCurrentTabActive &&
            isSupervisorInterface &&
            (
                type === "queue_join" ||
                type === "queue_message" ||
                type === "connect"
            )

        /*
         * Agent notifications that should be automatically
         * marked as read while actively viewing the agent
         * interface.
         */
        const shouldAutoReadAsAgent =
            isCurrentTabActive &&
            isAgentInterface &&
            (
                type === "queue_join" ||
                type === "queue_message" ||
                type === "chat_message"
            )

        /*
         * If either condition is true, the notification should
         * still be stored, but immediately considered read.
         */
        const shouldAutoRead =
            shouldAutoReadAsSupervisor ||
            shouldAutoReadAsAgent

        const readAt = shouldAutoRead
            ? new Date().toISOString()
            : null

        const item = {
            id: notification.id,
            notification: notification,
            read_at: readAt,
            dismissed_at: null,
        }

        /*
         * Always add the notification to the local notification
         * items, even when it is automatically marked as read.
         */
        setNotificationItems(current => [
            item,
            ...current,
        ])

        /*
         * Persist the automatically-read state in the backend.
         */
        if (shouldAutoRead) {
            api.patch(
                `api/users/${getSession().id}/notifications/${item.id}/`,
                {
                    read_at: readAt
                }
            ).catch(console.error)
        }

        setNotifications(current => {

            /*
             * Non-message notifications always get
             * their own group.
             */
            if (!isMessage) {
                return [
                    {
                        item,
                        items: [item],
                        unreadCount: shouldAutoRead ? 0 : 1,
                    },
                    ...current,
                ]
            }

            /*
             * Message without unique_id is standalone.
             */
            if (!uniqueId) {
                return [
                    {
                        item,
                        items: [item],
                        unreadCount: shouldAutoRead ? 0 : 1,
                    },
                    ...current,
                ]
            }

            /*
             * Find an existing group for this chat.
             */
            const groupIndex = current.findIndex(group => {
                const groupNotification = group.item.notification

                return (
                    (
                        groupNotification.type === "queue_message" ||
                        groupNotification.type === "chat_message"
                    ) &&
                    groupNotification.context?.unique_id === uniqueId
                )
            })

            /*
             * No existing group.
             */
            if (groupIndex === -1) {
                return [
                    {
                        item,
                        items: [item],
                        unreadCount: shouldAutoRead ? 0 : 1,
                    },
                    ...current,
                ]
            }

            /*
             * Existing group.
             */
            const group = current[groupIndex]

            const updatedGroup = {
                ...group,

                // Latest notification becomes representative
                item,

                items: [
                    ...group.items,
                    item,
                ],

                /*
                 * Don't increment unread count when this
                 * notification was automatically marked as read.
                 */
                unreadCount:
                    group.unreadCount +
                    (shouldAutoRead ? 0 : 1),
            }

            /*
             * Move the updated group to the top.
             */
            return [
                updatedGroup,
                ...current.filter((_, index) => index !== groupIndex),
            ]
        })

        /*
         * If the notification was automatically marked as read
         * because the user is actively using the relevant
         * interface, don't play a sound or show a favicon badge.
         */
        if (shouldAutoRead) {
            return
        }

        /*
         * Normal notification behavior.
         */
        notifyUser(notification)

        setFaviconBadge("message", true)

        setTimeout(() => {
            setFaviconBadge("message", false)
        }, 60000)
    }


    const loadNotifications = async () => {
        setLoadingNotifications(true)

        try {
            const userId = getSession().id

            const response = await api.get(
                `api/users/${userId}/notifications/`
            )

            const queues =
                getSession().profile?.enterprise?.queues || []

            const items = response.data.results.map(item => {
                if (item.notification.type !== "queue_join")
                    return item

                const queue = queues.find(
                    queue => queue.name === item.notification.subtitle
                )

                if (!queue)
                    return item

                return {
                    ...item,
                    notification: {
                        ...item.notification,
                        subtitle: `Fila: ${queue.description}`
                    }
                }
            })

            setNotificationItems(items)

            setNotifications(
                aggregateNotifications(items)
            )

            setNextNotificationsUrl(
                response.data.next
            )

        } catch (error) {
            console.error(
                "Error loading notifications:",
                error
            )

            setNotificationItems([])
            setNotifications([])
            setNextNotificationsUrl(null)

        } finally {
            setLoadingNotifications(false)
        }
    }


    const loadMoreNotifications = async () => {
        if (!nextNotificationsUrl || loadingMore)
            return

        setLoadingMore(true)

        try {
            const parsedUrl = new URL(nextNotificationsUrl)

            const response = await api.get(
                `${window.location.origin}/api${parsedUrl.pathname}${parsedUrl.search}`
            )

            const queues =
                getSession().profile?.enterprise?.queues || []

            const newItems = response.data.results.map(item => {
                if (item.notification.type !== "queue_join")
                    return item

                const queue = queues.find(
                    queue => queue.name === item.notification.subtitle
                )

                if (!queue)
                    return item

                return {
                    ...item,
                    notification: {
                        ...item.notification,
                        subtitle: `Fila: ${queue.description}`
                    }
                }
            })

            /*
             * Keep all notification items because the next page
             * may contain notifications belonging to groups that
             * already exist on the previous page.
             */
            const allItems = [
                ...notificationItems,
                ...newItems,
            ]

            setNotificationItems(allItems)

            /*
             * Rebuild the groups so notifications from the new
             * page are merged into existing chat groups.
             */
            setNotifications(
                aggregateNotifications(allItems)
            )

            setNextNotificationsUrl(
                response.data.next
            )

        } catch (error) {
            console.error(
                "Error loading more notifications:",
                error
            )

        } finally {
            setLoadingMore(false)
        }
    }


    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget)
    }


    const handleClose = () => {
        setAnchorEl(null)
    }


    const handleNotificationClick = async (group) => {

        const item = group.item
        const notification = item.notification

        const clickableTypes = [
            "queue_join",
            "connect",
            "queue_message",
            "chat_message",
        ]

        /*
         * Some notification types may not have a navigation
         * action.
         */
        if (!clickableTypes.includes(notification.type))
            return


        /*
         * Mark every unread notification in this group as read.
         *
         * Example:
         *
         * item 1 -> read
         * item 2 -> unread
         * item 3 -> unread
         *
         * Clicking the group marks item 2 and 3 as read.
         */
        const unreadItems = group.items.filter(
            item => !item.read_at
        )


        if (unreadItems.length > 0) {

            const readAt = new Date().toISOString()

            try {

                await Promise.all(
                    unreadItems.map(item =>
                        api.patch(
                            `api/users/${getSession().id}/notifications/${item.id}/`,
                            {
                                read_at: readAt
                            }
                        )
                    )
                )


                /*
                 * Update the local grouped state.
                 */
                setNotifications(current =>
                    current.map(currentGroup => {

                        if (currentGroup !== group)
                            return currentGroup

                        return {
                            ...currentGroup,
                            unreadCount: 0,

                            items: currentGroup.items.map(item => ({
                                ...item,
                                read_at: item.read_at || readAt
                            }))
                        }
                    })
                )

            } catch (error) {

                console.error(
                    "Error marking notifications as read:",
                    error
                )

                /*
                 * Don't navigate if we couldn't persist the
                 * read state.
                 */
                return
            }
        }


        /*
         * Navigate to the associated chat.
         */
        const uniqueId = notification.context?.unique_id

        if (!uniqueId)
            return


        const session = getSession()

        const isSupervisor = session.groups?.some(
            group => group.name?.toLowerCase() === "supervisor"
        )


        const selectedTab = isSupervisor
            ? "supervisor"
            : "chat"

        const notificationList = {
            queue_join: "queue",
            queue_message: "queue",
            connect: "onchat",
            chat_message: "onchat",
        }

        const list = notificationList[notification.type]

        if (!list)
            return

        history.push(
            `/chat?selectedTab=${selectedTab}&uniqueId=${encodeURIComponent(uniqueId)}&list=${list}`
        )

        setAnchorEl(null)
    }


    const handleDismiss = async (event, group) => {

        event.stopPropagation()

        const dismissedAt = new Date().toISOString()

        try {

            /*
             * Dismiss every notification represented by this
             * visual group.
             */
            await Promise.all(
                group.items.map(item =>
                    api.patch(
                        `api/users/${getSession().id}/notifications/${item.id}/`,
                        {
                            dismissed_at: dismissedAt
                        }
                    )
                )
            )


            /*
             * Remove the entire group from the UI.
             */
            setNotifications(current =>
                current.filter(
                    currentGroup => currentGroup !== group
                )
            )

        } catch (error) {

            console.error(
                "Error dismissing notifications:",
                error
            )
        }
    }


    const markAllAsRead = async () => {
        try {
            await api.patch(`api/users/${getSession().id}/notifications/read_all/`)

            setNotifications(prev =>
                prev.map(group => ({
                    ...group,
                    unreadCount: 0,
                    notifications: group.notifications?.map(notification => ({
                        ...notification,
                        read_at: notification.read_at || new Date().toISOString(),
                    })),
                }))
            )

            setUnreadCount(0)
        } catch (error) {
            console.error("Erro ao marcar notificações como lidas:", error)
        }
    }

    const dismissAll = async () => {
        try {
            await api.patch(`api/users/${getSession().id}/notifications/dismiss_all/`)

            setNotifications([])
            setUnreadCount(0)
        } catch (error) {
            console.error("Erro ao dispensar notificações:", error)
        }
    }


    useEffect(() => {
        loadNotifications()
    }, [])


    useEffect(() => {
        if (!realtimeNotification)
            return

        addRealtimeNotification(realtimeNotification)
    }, [realtimeNotification])


    const getNotificationSubtitle = (subtitle) => {
        if (!subtitle) {
            return null
        }

        // Direct media string
        if (typeof subtitle === "string") {
            const lowerSubtitle = subtitle.toLowerCase()

            if (lowerSubtitle.startsWith("(emailinbox)")) {
                return {
                    type: "email",
                    label: "Mensagem de e-mail",
                }
            }

            if (lowerSubtitle.startsWith("wpp-image:")) {
                return {
                    type: "image",
                    label: "Foto",
                }
            }

            if (lowerSubtitle.startsWith("wpp-document:")) {
                return {
                    type: "file",
                    label: "Documento",
                }
            }

            if (lowerSubtitle.startsWith("wpp-video:")) {
                return {
                    type: "video",
                    label: "Vídeo",
                }
            }

            if (lowerSubtitle.startsWith("wpp-audio:")) {
                return {
                    type: "audio",
                    label: "Áudio",
                }
            }
        }

        // JSON media
        try {
            const data = typeof subtitle === "string"
                ? JSON.parse(subtitle)
                : subtitle

            const message = data?.message?.toLowerCase()

            if (message?.startsWith("[url=")) {
                return {
                    type: "file",
                    label: "Documento",
                }
            }

            if (message?.startsWith("[video]")) {
                return {
                    type: "video",
                    label: "Vídeo",
                }
            }

            if (message?.startsWith("[img]")) {
                return {
                    type: "image",
                    label: "Foto",
                }
            }

            if (message?.startsWith("[audio]")) {
                return {
                    type: "audio",
                    label: "Áudio",
                }
            }
        } catch (error) {
            // Not JSON, render as normal text.
        }

        return {
            type: "text",
            label: emojiReplaceJSX(subtitle),
        }
    }


    return (
        <div className="notifications-menu-container">

            <IconButton
                className="notifications-icon"
                onClick={handleOpen}
            >
                <Badge
                    badgeContent={unreadCount}
                    max={99}
                >
                    <NotificationsIcon
                        className="toolbar-notifications-icon"
                    />
                </Badge>
            </IconButton>


            <Menu
                anchorEl={anchorEl}
                keepMounted
                sx={{ zIndex: 2001 }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                elevation={0}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center"
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "center"
                }}
                className="notifications-container"
                MenuListProps={{
                    disablePadding: true
                }}
            >

                <div className="notifications-header">

                    <div>

                        <div className="notifications-title">
                            Central de Notificações
                        </div>


                        {unreadCount > 0 && (
                            <div className="notifications-unread-count">
                                {unreadCount} não lida
                                {unreadCount > 1 ? "s" : ""}
                            </div>
                        )}

                    </div>

                    <div className="notifications-actions">

                        <IconButton
                            size="small"
                            title={
                                soundEnabled
                                    ? "Desativar sons"
                                    : "Ativar sons"
                            }
                            onClick={() => {
                                const newValue = !soundEnabled

                                setSoundEnabled(newValue)

                                localStorage.setItem(
                                    "notificationsSoundEnabled",
                                    String(newValue)
                                )
                            }}
                        >
                            {soundEnabled ? (
                                <VolumeUpIcon fontSize="small" />
                            ) : (
                                <VolumeOffIcon fontSize="small" />
                            )}
                        </IconButton>

                        <IconButton
                            size="small"
                            title={
                                desktopNotificationsEnabled
                                    ? "Desativar notificações da área de trabalho"
                                    : "Ativar notificações da área de trabalho"
                            }
                            onClick={() => {
                                const newValue = !desktopNotificationsEnabled

                                setDesktopNotificationsEnabled(newValue)

                                localStorage.setItem(
                                    "desktopNotificationsEnabled",
                                    String(newValue)
                                )
                            }}
                        >
                            {desktopNotificationsEnabled ? (
                                <NotificationsActiveIcon fontSize="small" />
                            ) : (
                                <NotificationsOffIcon fontSize="small" />
                            )}
                        </IconButton>

                        <IconButton
                            size="small"
                            title="Marcar todas como lidas"
                            onClick={markAllAsRead}
                            disabled={unreadCount === 0}
                        >
                            <DoneAll fontSize="small" />
                        </IconButton>

                        <IconButton
                            size="small"
                            title="Dispensar todas"
                            onClick={dismissAll}
                            disabled={notifications.length === 0}
                        >
                            <DeleteSweep fontSize="small" />
                        </IconButton>

                    </div>

                </div>


                <Divider />


                <div className="notifications-list">

                    {loadingNotifications ? (
                        <div className="notifications-empty">
                            <div>
                                Carregando notificações...
                            </div>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="notifications-empty">
                            <NotificationsIcon />
                            <div>
                                Nenhuma notificação
                            </div>
                        </div>
                    ) : (

                        notifications.map(group => {

                            const item = group.item
                            const notification = item.notification

                            const isUnread =
                                group.unreadCount > 0


                            /*
                             * If there are multiple unread messages,
                             * show the number instead of the generic
                             * notification title.
                             */
                            const title =
                                group.unreadCount > 1 &&
                                (
                                    notification.type === "queue_message" ||
                                    notification.type === "chat_message"
                                )
                                    ? `${group.unreadCount} novas mensagens no atendimento`
                                    : notification.title


                            return (
                                <MenuItem
                                    key={item.id}

                                    className={`
                                        notification-item
                                        notification-${notification.level}
                                        ${isUnread
                                            ? "notification-unread"
                                            : ""
                                        }
                                    `}

                                    onClick={() =>
                                        handleNotificationClick(group)
                                    }
                                >

                                    <div className="notification-icon">
                                        {getLevelIcon(
                                            notification.level
                                        )}
                                    </div>


                                    <div className="notification-content">

                                        <div className="notification-top">

                                            <div className="notification-title">
                                                {title}
                                            </div>


                                            <button
                                                className="notification-dismiss"

                                                onClick={(event) =>
                                                    handleDismiss(
                                                        event,
                                                        group
                                                    )
                                                }
                                            >
                                                <CloseIcon />
                                            </button>

                                        </div>


                                        {notification.subtitle && (() => {
                                            const subtitle = getNotificationSubtitle(notification.subtitle)

                                            if (!subtitle) {
                                                return null
                                            }

                                            return (
                                                <div className="notification-subtitle">
                                                    {subtitle.type === "image" && <ImageIcon fontSize="small" />}
                                                    {subtitle.type === "video" && <VideocamIcon fontSize="small" />}
                                                    {subtitle.type === "audio" && <AudiotrackIcon fontSize="small" />}
                                                    {subtitle.type === "file" && <AttachFileIcon fontSize="small" />}
                                                    {subtitle.type === "email" && <EmailIcon fontSize="small" />}

                                                    <span>{subtitle.label}</span>
                                                </div>
                                            )
                                        })()}


                                        <div className="notification-time">
                                            {formatNotificationDate(
                                                notification.created_at
                                            )}
                                        </div>

                                    </div>

                                </MenuItem>
                            )
                        })
                    )}

                </div>


                {notifications.length > 0 && (
                    <>
                        <Divider />

                        <div
                            className={`notifications-footer ${
                                loadingMore || !nextNotificationsUrl ? "disabled" : ""
                            }`}
                            onClick={
                                !loadingMore && nextNotificationsUrl
                                    ? loadMoreNotifications
                                    : undefined
                            }
                        >
                            {loadingMore
                                ? "Carregando..."
                                : nextNotificationsUrl
                                    ? "Ver mais notificações"
                                    : "Não há mais notificações"
                            }
                        </div>
                    </>
                )}

            </Menu>

        </div>
    )
}


export default NotificationsMenu
